import {
  App, Badge, Button,
  Flex, Image, Input,
  message, Space, Table,
  Tabs, TabsProps, Tooltip,
  Typography
} from "antd";
import {get, isEmpty} from "lodash";
import {useQuery} from "@tanstack/react-query";
import axiosInstance, {endpoints} from "../../services/axios";
import {useSearchParams} from "react-router-dom";
import {useMemo, useState} from "react";
import Filter from "./filter";
import {UserOutlined} from "@ant-design/icons";
import {formatDate} from "../../utils/functions";
import type {TableProps} from 'antd';

interface Props {
  permissions: Record<string, boolean>;
}

interface IFilter {
  year?: number;
  month?: number;
  departmentIds?: number[];
  day?: number;
  companyIds?: number[];
}

interface DataType {
  key: string;
  title: string;
}

const ExplanationLetter = ({permissions}: Props) => {
  const {modal} = App.useApp();
  const [searchParams] = useSearchParams();
  const isAdmin = get(permissions, "canReadExplanationLetter");
  const [filter, setFilter] = useState<Partial<IFilter>>({});
  const [searchTerm, setSearchTerm] = useState("");
  const [activeTabKey, setActiveTabKey] = useState("1");
  const [pagination, setPagination] = useState(() => {
    const saved = localStorage.getItem("explanation-pagination");
    return saved ? JSON.parse(saved) : {current: 1, pageSize: 50};
  });

  const {data: formData, isFetching: filterFetching, refetch} = useQuery({
    queryKey: ["explanation-letter", filter],
    queryFn: async () => {
      axiosInstance.defaults.headers.common.Authorization = searchParams.get("token");
      return await axiosInstance.get(endpoints.explanationLetter.list(get(filter, "year", ""), get(filter, "month", ""), get(filter, "departmentIds", []), get(filter, "day", null), get(filter, "companyIds", [])));
    },
    enabled: !!filter.year || !!filter.month
  });

  const pendingUsersList: any = useMemo(() => {
    return get(formData, "data.data.pendingItems", [])
  }, [formData]);
  const closedUsersList: any = useMemo(() => {
    return get(formData, "data.data.closedItems", [])
  }, [formData]);

  const handleTableChange = (paginationConfig: any) => {
    const updated = {
      current: paginationConfig.current,
      pageSize: paginationConfig.pageSize,
    };
    setPagination(updated);
    localStorage.setItem("explanation-pagination", JSON.stringify(updated));
  };
  const filteredList = useMemo(() => {
    const list = activeTabKey === "1" ? pendingUsersList : closedUsersList;

    if (!searchTerm.trim()) return list;
    return list.filter((item: any) => {
      const fullName = get(item, "user.fio", "").toLowerCase()

      return fullName.includes(searchTerm.toLowerCase());
    });
  }, [searchTerm, activeTabKey, pendingUsersList, closedUsersList]);

  const searchUser = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value);
  };
  const confirmChange = (row: any) => () => {
    modal.confirm({
      title: "Tushuntirish xati olish",
      content: "Rostdan ham tushuntirish xati olishni istaysizmi?",
      onOk: async () =>
          await axiosInstance
              .post(endpoints.explanationLetter.changeStatus, {
                id: get(row, "id", ''),
                ignoreType: get(row, "ignoreType", '')
              })
              .then(() => {
                refetch();
                message.success("Successfully changed status!");
              })
              .catch(() => ""),

      okText: "Xa",
      cancelText: "Yo'q",
    });
  };

  const pendingColumns: TableProps<DataType>['columns'] = [
    {
      title: "№",
      dataIndex: "no",
      render: (_: any, i: any, index: number) => index + 1,
      width: 20,
    },
    {
      title: "Kompaniya",
      dataIndex: ["company", "name"],
      key: "name"
    },
    {
      title: "Rasm",
      width: 80,
      render: (value: any) => <>{value.user.photo ?
          <Image className="rounded-full !w-[50px] !h-[50px] object-cover" src={value.user.photo}
                 alt={value.user.name}/> :
          <div className="w-[50px] h-[50px] flex justify-center items-center rounded-full bg-gray-100"><UserOutlined
              style={{fontSize: 32}}/></div>}</>
    },
    {
      title: "F.I.SH",
      dataIndex: ["user", "fio"],
      key: "fio",
      width: 300
    },
    {
      title: "Sabab",
      key: "typeStr",
      render: (value: any) => <Tooltip className="line-clamp-1 " placement="right"
                                       title={value.typeStr}>{value.typeStr}</Tooltip>
    },
    {
      title: "Sana",
      key: "date",
      render: (value: any) => <span>{formatDate(value.date)}</span>
    },
    {
      title: "Harakat",
      key: "id",
      width: 100,
      render: (value: any) => {
        if (permissions?.canChangeExplanationLetter && value.id !== null && value.id !== undefined) {
          return (
              <Button type="primary"
                      onClick={confirmChange({id: get(value, "id", ""), ignoreType: "EXPLANATION_LETTER",})}
              >
                Tushuntirish xati olish
              </Button>
          );
        }
        return null;
      },
    }
  ];
  const closedColumns: TableProps<DataType>['columns'] = [
    {
      title: "№",
      dataIndex: "no",
      render: (_: any, i: any, index: number) => index + 1,
      width: 20,
    },
    {
      title: "Kompaniya",
      dataIndex: ["company", "name"],
      key: "name",
      width: 140
    },
    {
      title: "Rasm",
      width: 80,
      render: (value: any) => <>{value.user.photo ?
          <Image className="rounded-full !w-[50px] !h-[50px] object-cover" src={value.user.photo}
                 alt={value.user.name}/> :
          <div className="w-[50px] h-[50px] flex justify-center items-center rounded-full bg-gray-100"><UserOutlined
              style={{fontSize: 32}}/></div>}</>
    },
    {
      title: "F.I.SH",
      dataIndex: ["user", "fio"],
      key: "fio",
      width: 300
    },
    {
      title: "Sabab",
      key: "typeStr",
      render: (value: any) => <Tooltip className="line-clamp-1 " placement="right"
                                       title={value.typeStr}>{value.typeStr}</Tooltip>
    },
    {
      title: "Sana",
      key: "date",
      width: 150,
      render: (value: any) => <span>{formatDate(value.date)}</span>
    },
    {
      title: "Rasm",
      width: 80,
      render: (value: any) => <>{value?.closedUser?.photo ?
          <Image className="rounded-full !w-[50px] !h-[50px] object-cover" src={value?.closedUser?.photo}
                 alt={value.user.name}/> :
          <div className="w-[50px] h-[50px] flex justify-center items-center rounded-full bg-gray-100"><UserOutlined
              style={{fontSize: 32}}/></div>}</>
    },
    {
      title: "Kim tomonidan olingan",
      width: 300,
      render: (value: any) => <span>{get(value, "closedUser.fio", "User malumoti topilmadi")}</span>
    },
    {
      title: "Tushuntirish xati sanasi",
      key: "date",
      width: 150,
      render: (value: any) => <span>{formatDate(value.closedTime)}</span>
    },
  ]

  const items: TabsProps['items'] = [
    {
      key: '1',
      label: <Badge count={pendingUsersList.length} overflowCount={500}><span className="px-5">Olinmagan</span></Badge>,
      children: <Table bordered loading={filterFetching} rowKey={"id"} dataSource={filteredList}
                       columns={pendingColumns} pagination={{
        current: pagination.current,
        pageSize: pagination.pageSize,
        showSizeChanger: true,
        pageSizeOptions: ["50", "100", "500"],
      }} onChange={handleTableChange}/>
    },
    {
      key: '2',
      label: <Badge count={closedUsersList.length} overflowCount={500}><span className="px-6">Olingan</span></Badge>,
      children: <Table bordered loading={filterFetching} rowKey={"id"} dataSource={filteredList} columns={closedColumns}
                       pagination={{
                         current: pagination.current,
                         pageSize: pagination.pageSize,
                         showSizeChanger: true,
                         pageSizeOptions: ["50", "100", "500"],
                       }} onChange={handleTableChange}/>
    },
  ];

  const onChange = (key: string) => {
    setActiveTabKey(key);
  };

  return (
      <div>
        <Flex align="center" justify="space-between" className="">
          <Typography.Title className="mt-0" level={4}>Tushuntirish xati</Typography.Title>

          <Space>
            <Input value={searchTerm} onChange={searchUser} placeholder="Search users..."/>
            <Filter isEmpty={isEmpty(filter)} isAdmin={isAdmin} onFilterChange={setFilter}/>
          </Space>
        </Flex>

        <Tabs defaultActiveKey="1" items={items} onChange={onChange}/>
      </div>
  )
}
export default ExplanationLetter